import { useCallback, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { Banner, Field, Modal } from './ui';
import { useSession } from '../state/session';

type Pending = { enrolled: boolean; retry: () => Promise<void> };

/**
 * Step-up authentication for administrative work.
 *
 * Billing, people, API keys and retention require a verified second factor. The
 * server answers `mfa_enrollment_required` or `mfa_required`; rather than
 * dead-ending the user, this hook catches those, collects what is missing, and
 * replays the original action.
 */
export function useMfaGuard() {
  const { refresh } = useSession();
  const [pending, setPending] = useState<Pending | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauth, setOtpauth] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Wrap an action so an MFA challenge is handled transparently. */
  const guard = useCallback(async (action: () => Promise<void>) => {
    try {
      await action();
    } catch (cause) {
      if (cause instanceof ApiError &&
          (cause.code === 'mfa_required' || cause.code === 'mfa_enrollment_required')) {
        const enrolled = cause.code === 'mfa_required';
        setError(null);
        setCode('');
        if (!enrolled) {
          const begin = await api.post('/auth/mfa/begin');
          setSecret(begin.secret);
          setOtpauth(begin.otpauth_url);
        } else {
          setSecret(null);
          setOtpauth(null);
        }
        setPending({ enrolled, retry: action });
        return;
      }
      throw cause;
    }
  }, []);

  const submit = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(pending.enrolled ? '/auth/mfa/verify' : '/auth/mfa/confirm', { code });
      await refresh();
      const retry = pending.retry;
      setPending(null);
      setCode('');
      await retry();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That code did not match.');
    } finally {
      setBusy(false);
    }
  }, [pending, code, refresh]);

  const challenge = pending ? (
    <Modal
      title={pending.enrolled ? 'Confirm your authenticator code'
                              : 'Set up two-factor authentication'}
      onClose={() => { setPending(null); setCode(''); setError(null); }}
      actions={
        <>
          <button className="btn" onClick={() => { setPending(null); setCode(''); }}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={busy || code.length !== 6}
                  onClick={() => void submit()}>
            {busy ? 'Checking…' : pending.enrolled ? 'Confirm' : 'Enable and continue'}
          </button>
        </>
      }
    >
      {error && <Banner tone="danger" title="That did not work">{error}</Banner>}
      {!pending.enrolled && (
        <>
          <p className="small muted">
            Billing, people, API keys and retention are protected by a second
            factor. Add this secret to an authenticator app, then enter the code
            it shows.
          </p>
          {secret && <pre className="codebox" style={{ marginBottom: 12 }}>{secret}</pre>}
          {otpauth && (
            <p className="small muted" style={{ wordBreak: 'break-all' }}>
              Or open: <code>{otpauth}</code>
            </p>
          )}
        </>
      )}
      <Field label="Six-digit code" id="mfa-guard-code" required>
        <input id="mfa-guard-code" className="input" inputMode="numeric"
               autoComplete="one-time-code" maxLength={6} value={code}
               style={{ fontSize: '1.3rem', letterSpacing: '.3em', textAlign: 'center' }}
               onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} />
      </Field>
    </Modal>
  ) : null;

  return { guard, challenge };
}
